import * as actionTypes from "../actionTypes";
import * as tabContentKeys from "../enums/tabContentKeys";
import createReducer from "../lib/createReducer";
import findIndex from "lodash.findindex";
import initialContentValue from "../lib/initialContentValue";
import isEqual from "lodash.isequal";

const defaultState = {};

export const getKey = (binderId, tabId) => {
	if (binderId === null || binderId === undefined) {
		throw new Error("Cannot get key: invalid binder id");
	}
	if (tabId === null || tabId === undefined) {
		throw new Error("Cannot get key: invalid tab id");
	}

	return `${binderId}-${tabId}`;
};

function BINDER_TABS_SET(state, action) {
	const { tabs } = action;
	if (tabs.length === 0) {
		return state;
	}

	const nextState = { ...state };
	tabs.forEach(t => {
		const key = getKey(t.binderId, t.id);
		nextState[key] = {
			binderId: t.binderId,
			clientId: t.clientId,
			content: t.content,
			dataWarningShown: t.dataWarningShown || false,
			deniedURL: t.deniedURL,
			dirty: t.dirty,
			editable: t.editable,
			editCriterionName: t.editCriterionName,
			editJoinpathName: t.editJoinpathName,
			featureRequired: t.featureRequired,
			id: t.id,
			linkId: t.link_id,
			patentCloud: t.patentCloud,
			iFrame: t.iFrame,
			search: t.search,
			sectionIdNames: t.sectionIdNames,
			tableau: t.tableau,
			title: t.title,
			generatedFromIframe: t.generatedFromIframe,
		};
	});

	return nextState;
}

function BINDER_SET(state, action) {
	const { binder } = action;
	if (!binder) {
		return state;
	}

	const tabs = binder.tabs;
	if (tabs === undefined || tabs === null) {
		return state;
	}

	const nextState = { ...state };
	tabs.forEach((t, index) => {
		const binderId = t.binder_id || binder.id; // NOTE(CK): Profile binder tabs don't have binder_id property
		const tabId = index;
		const key = getKey(binderId, tabId);

		const clientId = t.client_id !== null && t.client_id !== undefined ? t.client_id : null;

		nextState[key] = {
			binderId,
			clientId,
			content: t.content,
			dataWarningShown: false,
			deniedURL: t.deniedURL,
			dirty: false,
			editable: t.editable,
			editCriterionName: t.edit_criterion_name,
			editJoinpathName: t.edit_joinpath_name,
			featureRequired: t.featureRequired,
			id: tabId,
			linkId: t.link_id,
			patentCloud: t.patent_cloud,
			iFrame: t.iFrame,
			search: t.search,
			tableau: t.tableau,
			title: t.title,
		};
	});

	return nextState;
}

function BINDERS_REMOVE(state, action) {
	const { binderId } = action;
	if (binderId === undefined || binderId === null) {
		return state;
	}

	const nextState = { ...state };
	Object.keys(state).forEach(key => {
		const tab = state[key];
		if (tab.binderId === binderId) {
			delete nextState[key];
		}
	});
	return nextState;
}

function BINDER_TABS_REMOVE(state, action) {
	const { binderId, tabId } = action;

	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab) {
		return state;
	}

	const nextState = { ...state };
	delete nextState[key];

	return nextState;
}

function BINDER_TABS_SET_TITLE(state, action) {
	const { binderId, tabId, title } = action;

	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab || !title) {
		return state;
	}

	return {
		...state,
		[key]: {
			...binderTab,
			dirty: true,
			title,
		},
	};
}

function BINDER_TABS_SET_SEARCH_TARGET(state, action) {
	const { binderId, tabId, search, title } = action;

	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab) {
		return state;
	}

	const newBinderTab = {
		...binderTab,
		dirty: true,
		search,
		content: {
			...binderTab.content,
			[tabContentKeys.IN_PROGRESS_SEARCH]: true,
		},
		title,
	};

	delete newBinderTab.content.library_name;
	delete newBinderTab.content.profile_type;
	delete newBinderTab.content.profile_id;

	return {
		...state,
		[key]: newBinderTab,
	};
}

function BINDER_TABS_SET_SEARCH_FIELD_NAMES(state, action) {
	const { binderId, tabId, fieldNames } = action;

	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab) {
		return state;
	}

	return {
		...state,
		[key]: {
			...binderTab,
			search: {
				...binderTab.search,
				field_names: fieldNames,
			},
			dirty: true,
		},
	};
}

function BINDER_TABS_SET_PROFILE(state, action) {
	const { binderId, tabId, libraryName, profileType, profileId, title } = action;

	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab) {
		return state;
	}

	return {
		...state,
		[key]: {
			...binderTab,
			dirty: true,
			search: null,
			content: {
				[tabContentKeys.LIBRARY_NAME]: libraryName,
				[tabContentKeys.PROFILE_TYPE]: profileType,
				[tabContentKeys.PROFILE_ID]: profileId,
			},
			title,
		},
	};
}

function BINDER_TABS_SET_SEARCH_COL_ORDER(state, action) {
	const { binderId, tabId, columnOrder } = action;

	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab) {
		return state;
	}

	const nextContent = {
		...binderTab.content,
	};
	if (columnOrder) {
		nextContent[tabContentKeys.COLUMN_ORDER_KEY] = columnOrder;
	} else {
		delete nextContent[tabContentKeys.COLUMN_ORDER_KEY];
	}

	return {
		...state,
		[key]: {
			...binderTab,
			dirty: true,
			content: nextContent,
		},
	};
}

function BINDER_TABS_SET_SEARCH_COL_WIDTH(state, action) {
	const { binderId, tabId, columnWidths } = action;

	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab) {
		return state;
	}

	const nextContent = {
		...binderTab.content,
	};
	if (columnWidths) {
		nextContent[tabContentKeys.COLUMN_WIDTH_KEY] = columnWidths;
	} else {
		delete nextContent[tabContentKeys.COLUMN_WIDTH_KEY];
	}

	return {
		...state,
		[key]: {
			...binderTab,
			dirty: true,
			content: nextContent,
		},
	};
}

function BINDER_TABS_SET_SEARCH_SORT(state, action) {
	const { binderId, tabId, sortOrder } = action;

	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab) {
		return state;
	}

	return {
		...state,
		[key]: {
			...binderTab,
			dirty: true,
			content: {
				...binderTab.content,
				[tabContentKeys.SORT_ORDER_KEY]: sortOrder,
			},
		},
	};
}

function BINDER_TABS_SET_ANAYLTICS_CHART_META(state, action) {
	const { binderId, tabId, tablePercent, chartPercent, chartStyle, estimated, numbers } = action;

	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab) {
		return state;
	}

	const content = {
		...binderTab.content,
	};
	if (tablePercent !== undefined && tablePercent !== null) {
		content[tabContentKeys.TABLE_PERCENT_KEY] = tablePercent;
	}
	if (chartPercent !== undefined && chartPercent !== null) {
		content[tabContentKeys.CHART_PERCENT_KEY] = chartPercent;
	}
	if (chartStyle !== undefined && chartStyle !== null) {
		content[tabContentKeys.CHART_STYLE_KEY] = chartStyle;
	}
	if (estimated !== undefined && estimated !== null) {
		content[tabContentKeys.ESTIMATED_KEY] = estimated;
	}
	if (numbers !== undefined && numbers !== null) {
		content[tabContentKeys.NUMBERS_KEY] = numbers;
	}
	return {
		...state,
		[key]: {
			...binderTab,
			dirty: true,
			content,
		},
	};
}

function BINDER_TABS_SET_ANAYLTICS_TABLE_META(state, action) {
	const { binderId, tabId, count, min, q0, q1, q2, q3, q4, max, average } = action;

	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab) {
		return state;
	}

	const content = {
		...binderTab.content,
	};
	if (count !== undefined && count !== null) {
		content[tabContentKeys.COUNT_KEY] = count;
	}
	if (min !== undefined && min !== null) {
		content[tabContentKeys.MIN_KEY] = min;
	}
	if (q0 !== undefined && q0 !== null) {
		content[tabContentKeys.Q0_KEY] = q0;
	}
	if (q1 !== undefined && q1 !== null) {
		content[tabContentKeys.Q1_KEY] = q1;
	}
	if (q2 !== undefined && q2 !== null) {
		content[tabContentKeys.Q2_KEY] = q2;
	}
	if (q3 !== undefined && q3 !== null) {
		content[tabContentKeys.Q3_KEY] = q3;
	}
	if (q4 !== undefined && q4 !== null) {
		content[tabContentKeys.Q4_KEY] = q4;
	}
	if (max !== undefined && max !== null) {
		content[tabContentKeys.MAX_KEY] = max;
	}
	if (average !== undefined && average !== null) {
		content[tabContentKeys.AVERAGE_KEY] = average;
	}

	return {
		...state,
		[key]: {
			...binderTab,
			dirty: true,
			content,
		},
	};
}

function BINDER_TABS_SET_PROFILE_SECTION_EXPANDED(state, action) {
	const { binderId, tabId, sectionKey, expanded } = action;

	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab || !sectionKey || expanded === undefined || expanded === null) {
		return state;
	}

	const content = {
		...binderTab.content,
		[sectionKey]: expanded,
	};

	return {
		...state,
		[key]: {
			...binderTab,
			dirty: true,
			content,
		},
	};
}

function BINDER_TABS_SET_DATA_WARNING_SHOWN(state, action) {
	const { binderId, tabId } = action;
	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab) {
		return state;
	}

	return {
		...state,
		[key]: {
			...binderTab,
			dataWarningShown: true,
		},
	};
}

function BINDER_TABS_SET_CHART_CONTENT(state, action) {
	const { binderId, tabId, chartContent } = action;
	const key = getKey(binderId, tabId);
	const binderTab = state[key];
	if (!binderTab) {
		return state;
	}

	return {
		...state,
		[key]: {
			...binderTab,
			dirty: true,
			content: {
				...binderTab.content,
				[tabContentKeys.CHART_CONTENT]: chartContent,
			},
		},
	};
}

function BINDER_TABS_SET_TILE_CONTENT(state, action) {
	const { binderTabId, tileId, tileContent } = action;
	const binderTab = state[binderTabId];
	if (!binderTab) {
		return state;
	}
	const tilesContent = initialContentValue(binderTab, tabContentKeys.TILES_CONTENT, {});
	return {
		...state,
		[binderTabId]: {
			...binderTab,
			dirty: true,
			content: {
				...binderTab.content,
				[tabContentKeys.TILES_CONTENT]: {
					...tilesContent,
					[tileId]: tileContent,
				},
			},
		},
	};
}

function BINDER_TABS_SET_TILE_SECTIONS(state, action) {
	const { binderTabId, sectionIdNames } = action;
	const binderTab = state[binderTabId];
	if (!binderTab) {
		return state;
	}

	return {
		...state,
		[binderTabId]: {
			...binderTab,
			sectionIdNames,
		},
	};
}

function BINDER_TABS_ADD_TILE_SECTION(state, action) {
	const { binderTabId, sectionId, name } = action;
	const binderTab = state[binderTabId];
	if (!binderTab) {
		return state;
	}

	const idName = { id: sectionId, name };
	return {
		...state,
		[binderTabId]: {
			...binderTab,
			sectionIdNames: binderTab.sectionIdNames
				? [...binderTab.sectionIdNames, idName]
				: [idName],
		},
	};
}

function BINDER_TABS_ADD_TABLEAU_FILTER(state, action) {
	const { binderTabId, filter } = action;

	if (!binderTabId || !filter) {
		return state;
	}
	const binderTab = state[binderTabId];
	if (!binderTab) {
		return state;
	}

	let filters = initialContentValue(binderTab, tabContentKeys.TABLEAU_FILTERS, []);
	const existingFilterIdx = findIndex(
		filters,
		f =>
			f.name === filter.name &&
			f.worksheetIndex === filter.worksheetIndex &&
			f.worksheetName === filter.worksheetName,
	);

	if (existingFilterIdx !== -1 && isEqual(filters[existingFilterIdx], filter)) {
		return state;
	}

	if (existingFilterIdx === -1) {
		filters = [...filters, filter];
	} else {
		filters = [
			...filters.slice(0, existingFilterIdx),
			filter,
			...filters.slice(existingFilterIdx + 1),
		];
	}

	return {
		...state,
		[binderTabId]: {
			...binderTab,
			dirty: true,
			content: {
				...binderTab.content,
				[tabContentKeys.TABLEAU_FILTERS]: filters,
			},
		},
	};
}

function BINDER_TABS_ADD_TABLEAU_PARAMETER(state, action) {
	const { binderTabId, parameter } = action;

	if (!binderTabId || !parameter) {
		return state;
	}
	const binderTab = state[binderTabId];
	if (!binderTab) {
		return state;
	}

	let parameters = initialContentValue(binderTab, tabContentKeys.TABLEAU_PARAMETERS, []);
	const existingParameterIdx = findIndex(parameters, p => p.name === parameter.name);

	if (existingParameterIdx !== -1 && isEqual(parameters[existingParameterIdx], parameter)) {
		return state;
	}

	if (existingParameterIdx === -1) {
		parameters = [...parameters, parameter];
	} else {
		parameters = [
			...parameters.slice(0, existingParameterIdx),
			parameter,
			...parameters.slice(existingParameterIdx + 1),
		];
	}

	return {
		...state,
		[binderTabId]: {
			...binderTab,
			dirty: true,
			content: {
				...binderTab.content,
				[tabContentKeys.TABLEAU_PARAMETERS]: parameters,
			},
		},
	};
}

function BINDER_TABS_RESET_TABLEAU_VALUES(state, action) {
	const { binderTabId } = action;

	if (!binderTabId) {
		return state;
	}
	const binderTab = state[binderTabId];
	if (!binderTab) {
		return state;
	}

	const filters = initialContentValue(binderTab, tabContentKeys.TABLEAU_FILTERS, []);
	const parameters = initialContentValue(binderTab, tabContentKeys.TABLEAU_PARAMETERS, []);
	if (!filters.length && !parameters.length) {
		return state;
	}

	const content = {
		...binderTab.content,
	};
	delete content[tabContentKeys.TABLEAU_FILTERS];
	delete content[tabContentKeys.TABLEAU_PARAMETERS];

	return {
		...state,
		[binderTabId]: {
			...binderTab,
			content,
		},
	};
}

function BINDER_TABS_SET_ROWS_HIDDEN(state, action) {
	const { binderTabId, rowIds } = action;

	if (!binderTabId || !rowIds || !rowIds.length) {
		return state;
	}
	const binderTab = state[binderTabId];
	if (!binderTab) {
		return state;
	}

	const hiddenRows = initialContentValue(binderTab, tabContentKeys.HIDDEN_ROWS, {});
	const nextHiddenRows = rowIds.reduce(
		(acc, id) => {
			acc[id] = true;
			return acc;
		},
		Array.isArray(hiddenRows) ? {} : { ...hiddenRows },
	);

	return {
		...state,
		[binderTabId]: {
			...binderTab,
			dirty: true,
			content: {
				...binderTab.content,
				[tabContentKeys.HIDDEN_ROWS]: nextHiddenRows,
			},
		},
	};
}

function BINDER_TABS_SET_ALL_ROWS_HIDDEN(state, action) {
	const { binderTabId } = action;

	if (!binderTabId) {
		return state;
	}

	const binderTab = state[binderTabId];
	if (!binderTab) {
		return state;
	}

	const nextContent = {
		...binderTab.content,
		[tabContentKeys.HIDDEN_ROWS_IS_ALL]: true,
	};
	delete nextContent[tabContentKeys.HIDDEN_ROWS];

	return {
		...state,
		[binderTabId]: {
			...binderTab,
			dirty: true,
			content: nextContent,
		},
	};
}

function BINDER_TABS_RESET_HIDDEN_ROWS(state, action) {
	const { binderTabId } = action;

	if (!binderTabId) {
		return state;
	}

	const binderTab = state[binderTabId];
	if (!binderTab) {
		return state;
	}

	const nextContent = {
		...binderTab.content,
	};
	delete nextContent[tabContentKeys.HIDDEN_ROWS];
	delete nextContent[tabContentKeys.HIDDEN_ROWS_IS_ALL];

	return {
		...state,
		[binderTabId]: {
			...binderTab,
			dirty: true,
			content: nextContent,
		},
	};
}

function BINDER_TABS_TOGGLE_SELECT_HIDDEN(state, action) {
	const { binderTabId } = action;

	if (!binderTabId) {
		return state;
	}

	const binderTab = state[binderTabId];
	if (!binderTab) {
		return state;
	}

	const prevContent = binderTab.content ? binderTab.content : {};

	const nextContent = {
		...prevContent,
	};

	if (prevContent[tabContentKeys.RESULTS_SELECT_HIDDEN]) {
		delete nextContent[tabContentKeys.RESULTS_SELECT_HIDDEN];
	} else {
		nextContent[tabContentKeys.RESULTS_SELECT_HIDDEN] = true;
	}

	return {
		...state,
		[binderTabId]: {
			...binderTab,
			dirty: true,
			content: nextContent,
		},
	};
}

const handlers = {};
handlers[actionTypes.BINDER_TABS_SET] = BINDER_TABS_SET;
handlers[actionTypes.BINDERS_SET] = BINDER_SET;
handlers[actionTypes.BINDERS_REMOVE] = BINDERS_REMOVE;
handlers[actionTypes.BINDER_TABS_REMOVE] = BINDER_TABS_REMOVE;
handlers[actionTypes.BINDER_TABS_SET_TITLE] = BINDER_TABS_SET_TITLE;
handlers[actionTypes.BINDER_TABS_SET_SEARCH_TARGET] = BINDER_TABS_SET_SEARCH_TARGET;
handlers[actionTypes.BINDER_TABS_SET_SEARCH_FIELD_NAMES] = BINDER_TABS_SET_SEARCH_FIELD_NAMES;
handlers[actionTypes.BINDER_TABS_SET_PROFILE] = BINDER_TABS_SET_PROFILE;
handlers[actionTypes.BINDER_TABS_SET_SEARCH_COL_ORDER] = BINDER_TABS_SET_SEARCH_COL_ORDER;
handlers[actionTypes.BINDER_TABS_SET_SEARCH_COL_WIDTH] = BINDER_TABS_SET_SEARCH_COL_WIDTH;
handlers[actionTypes.BINDER_TABS_SET_SEARCH_SORT] = BINDER_TABS_SET_SEARCH_SORT;
handlers[actionTypes.BINDER_TABS_SET_ANAYLTICS_CHART_META] = BINDER_TABS_SET_ANAYLTICS_CHART_META;
handlers[actionTypes.BINDER_TABS_SET_ANAYLTICS_TABLE_META] = BINDER_TABS_SET_ANAYLTICS_TABLE_META;
handlers[
	actionTypes.BINDER_TABS_SET_PROFILE_SECTION_EXPANDED
] = BINDER_TABS_SET_PROFILE_SECTION_EXPANDED;
handlers[actionTypes.BINDER_TABS_SET_DATA_WARNING_SHOWN] = BINDER_TABS_SET_DATA_WARNING_SHOWN;
handlers[actionTypes.BINDER_TABS_SET_CHART_CONTENT] = BINDER_TABS_SET_CHART_CONTENT;
handlers[actionTypes.BINDER_TABS_SET_TILE_CONTENT] = BINDER_TABS_SET_TILE_CONTENT;
handlers[actionTypes.BINDER_TABS_SET_TILE_SECTIONS] = BINDER_TABS_SET_TILE_SECTIONS;
handlers[actionTypes.BINDER_TABS_ADD_TILE_SECTION] = BINDER_TABS_ADD_TILE_SECTION;
handlers[actionTypes.BINDER_TABS_ADD_TABLEAU_FILTER] = BINDER_TABS_ADD_TABLEAU_FILTER;
handlers[actionTypes.BINDER_TABS_ADD_TABLEAU_PARAMETER] = BINDER_TABS_ADD_TABLEAU_PARAMETER;
handlers[actionTypes.BINDER_TABS_RESET_TABLEAU_VALUES] = BINDER_TABS_RESET_TABLEAU_VALUES;
handlers[actionTypes.BINDER_TABS_SET_ROWS_HIDDEN] = BINDER_TABS_SET_ROWS_HIDDEN;
handlers[actionTypes.BINDER_TABS_RESET_HIDDEN_ROWS] = BINDER_TABS_RESET_HIDDEN_ROWS;
handlers[actionTypes.BINDER_TABS_TOGGLE_SELECT_HIDDEN] = BINDER_TABS_TOGGLE_SELECT_HIDDEN;
handlers[actionTypes.BINDER_TABS_SET_ALL_ROWS_HIDDEN] = BINDER_TABS_SET_ALL_ROWS_HIDDEN;
export default createReducer(defaultState, handlers);
